import { useCallback, useEffect, useRef } from "react";

interface DebounceOptions {
  /** 是否第一次调用立即执行，默认为false */
  immediately?: boolean;
}

/**
 * 防抖hook
 * @param callback 回调函数
 * @param delay 防抖延迟时间，默认500ms
 * @param options 配置选项
 * @param options.immediately 是否第一次调用立即执行，默认为false
 * @returns 防抖后的回调函数
 */
export function useDebounce<
  T extends (...args: Parameters<T>) => ReturnType<T>
>(callback: T, delay: number = 500, options: DebounceOptions = {}) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstCallRef = useRef(true);
  const { immediately = false } = options;

  const debouncedCallback = useCallback((...args: Parameters<T>) => {
    // 处理第一次调用立即执行的情况
    if (isFirstCallRef.current && immediately) {
      isFirstCallRef.current = false;
      callback(...args);
      return;
    }

    // 标记不是第一次调用了
    isFirstCallRef.current = false;

    // 正常的防抖逻辑
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay, immediately]);

  useEffect(() => {
    // 组件卸载时清理定时器
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 当immediately选项改变时，重置首次调用标记
  useEffect(() => {
    isFirstCallRef.current = true;
  }, [immediately]);

  return debouncedCallback;
}